import { ReactNode, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Info,
  TriangleAlert,
  X,
} from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { Text } from './Text';
import { IconButton } from './IconButton';
import { Button } from './Button';
import { useT } from '../../modules/i18n';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

export interface SnackbarAction {
  label: string;
  onPress: () => void;
}

export interface SnackbarProps {
  open: boolean;
  severity?: SnackbarSeverity;
  title?: ReactNode;
  children?: ReactNode;
  onClose: () => void;
  action?: SnackbarAction;
  autoHideDuration?: number;
  placement?: 'top-center' | 'bottom-right' | 'top-right';
  className?: string;
}

// Per-severity look: a `tint` background + matching `border`, plus a colored
// icon `chip`. The tint layers over an opaque `bg-surface` base (below), so the
// toast reads as its severity color yet stays fully opaque (no see-through).
const severityConfig: Record<
  SnackbarSeverity,
  { tint: string; border: string; chip: string; icon: ReactNode }
> = {
  info: {
    tint: 'bg-brand-solid/10',
    border: 'border-brand-solid/40',
    chip: 'bg-brand-solid/20 text-brand-text',
    icon: <Icon as={Info} size="lg" />,
  },
  success: {
    tint: 'bg-success-500/10',
    border: 'border-success-500/40',
    chip: 'bg-success-500/20 text-success',
    icon: <Icon as={CheckCircle2} size="lg" />,
  },
  warning: {
    tint: 'bg-warning-500/10',
    border: 'border-warning-500/40',
    chip: 'bg-warning-500/20 text-warning',
    icon: <Icon as={TriangleAlert} size="lg" />,
  },
  error: {
    tint: 'bg-error-500/10',
    border: 'border-error-500/40',
    chip: 'bg-error-500/20 text-error',
    icon: <Icon as={AlertCircle} size="lg" />,
  },
};

const placementClasses: Record<
  NonNullable<SnackbarProps['placement']>,
  string
> = {
  // top-6 keeps a comfortable gap from the top edge.
  'top-center': 'fixed left-1/2 top-6 z-50 -translate-x-1/2',
  'bottom-right': 'fixed bottom-4 right-4 z-50',
  'top-right': 'fixed top-4 right-4 z-50',
};

/**
 * Toast / snackbar notification — fixed-position, severity-colored,
 * with optional dismiss and action button. Auto-hides after
 * `autoHideDuration` ms when provided. Reuses the Alert severity/token
 * config (success/error/warning/info). No Backstage imports — safe for
 * Storybook stories.
 */
export const Snackbar = ({
  open,
  severity = 'info',
  title,
  children,
  onClose,
  action,
  autoHideDuration,
  placement = 'top-center',
  className,
}: SnackbarProps) => {
  const t = useT();
  useEffect(() => {
    if (!open || !autoHideDuration) return undefined;
    const id = setTimeout(onClose, autoHideDuration);
    return () => clearTimeout(id);
  }, [open, autoHideDuration, onClose]);

  if (!open) return null;

  const c = severityConfig[severity];

  return (
    <div
      role="status"
      aria-live="polite"
      className={cx(
        placementClasses[placement],
        'w-[calc(100vw-2rem)] max-w-xl',
        className,
      )}
    >
      <div
        className={cx(
          'overflow-hidden rounded-2xl border bg-surface shadow-xl',
          c.border,
        )}
      >
        <div className={cx('flex items-start gap-3 p-4', c.tint)}>
          <span
            className={cx(
              'mt-0.5 inline-flex size-11 shrink-0 items-center justify-center rounded-full',
              c.chip,
            )}
          >
            {c.icon}
          </span>
          <div className="min-w-0 flex-1 pt-1">
            {title && (
              <Text variant="body" className="font-semibold text-fg">
                {title}
              </Text>
            )}
            {children && (
              <Text
                variant="body"
                tone="secondary"
                className={cx(title ? 'mt-0.5' : '')}
              >
                {children}
              </Text>
            )}
            {action && (
              <div className="mt-2">
                <Button variant="tertiary" size="sm" onPress={action.onPress}>
                  {action.label}
                </Button>
              </div>
            )}
          </div>
          <IconButton
            aria-label={t('ui.snackbar.dismissAriaLabel')}
            size="sm"
            onPress={onClose}
            className="-my-1 -mr-1 shrink-0"
          >
            <Icon as={X} size="sm" />
          </IconButton>
        </div>
      </div>
    </div>
  );
};
